// Weight-loss additive model (see docs/BACKEND_CATALOG_CHECKOUT_PRD.md §2):
// an additive is either COMPOUNDED into the product (swap the primary to the
// compounded SKU — one medId) or a STANDALONE SKU added as its own line.
//
// Detection is metadata-first: /catalog/medications items now carry
// additiveKeys (['glycine'], ['b6','b12'], … — [] for plain bases) and
// baseProductKey (groups a base with its compounded variants at the same
// molecule/dose/supply). The legacy string-parsing of `strength`/`name` is
// kept ONLY as a fallback for payloads without the metadata fields:
//   - beluga:   strength "Semaglutide + Glycine Injection 1mg/0.5mg/mL"
//   - southend: slash-form names, e.g. "Semaglutide/Pyridoxine (B6) 1mg/25mg"
// The `medication` field is NOT used for detection — beluga writes
// "Semaglutide + Glycine …" there on every WL SKU including plain bases,
// which would classify the whole catalog as glycine compounds.

// `key` is the FE/answer key; `beKeys` are the additiveKey values the BE
// emits for it (pyridoxine = b6 per BE; zofran ships as "ondansetron").
export const WL_ADDITIVES = [
  { key: 'glycine', beKeys: ['glycine'], label: 'Glycine', compoundRe: /glycine/i, standaloneRe: /^glycine\b/i },
  { key: 'b6', beKeys: ['b6'], label: 'B6 (Pyridoxine)', compoundRe: /\bb6\b|pyridoxine/i, standaloneRe: /^(b6|pyridoxine)\b/i },
  { key: 'zofran', beKeys: ['ondansetron'], label: 'Zofran (Ondansetron)', compoundRe: /zofran|ondansetron/i, standaloneRe: /^(zofran|ondansetron)\b/i },
  { key: 'l-carnitine', beKeys: ['carnitine', 'l-carnitine'], label: 'L-Carnitine', compoundRe: /l-?carnitine/i, standaloneRe: /^l-?carnitine\b/i },
  { key: 'b3', beKeys: ['niacin', 'b3'], label: 'B3 (Niacin)', compoundRe: /\bb3\b|niacin/i, standaloneRe: /^(b3|niacin)\b/i },
  { key: 'b12', beKeys: ['b12'], label: 'B12', compoundRe: /\bb12\b|cyanocobalamin|methylcobalamin/i, standaloneRe: /^b12\b/i },
];

export const ANSWER_TO_KEY = {
  glycine: 'glycine',
  'l-carnitine': 'l-carnitine',
  b6: 'b6',
  b12: 'b12',
  b3: 'b3',
  'zofran-prescribed': 'zofran',
};

// The BE's additive keys for an item, or null when the item predates the
// metadata (→ callers fall back to string parsing).
function additiveKeysOf(item) {
  if (Array.isArray(item?.additiveKeys)) return item.additiveKeys;
  if (item && 'additiveKey' in item) return item.additiveKey ? [item.additiveKey] : [];
  return null;
}

// Returns the additive(s) compounded into a SKU, or null for a plain base.
export function additiveOf(item) {
  const keys = additiveKeysOf(item);
  if (keys) return keys.length ? keys.join('+') : null;
  const strength = item?.strength || '';
  const plus = strength.match(/\+\s*(.+?)\s+Injection/i);
  if (plus) return plus[1].trim();
  const slash = `${item?.name || ''} ${strength}`.match(
    /(?:semaglutide|tirzepatide)\s*\/\s*([A-Za-z][A-Za-z0-9\- ()]*)/i,
  );
  if (slash) return slash[1].trim();
  return null;
}

// True when the SKU carries this additive compounded in.
function carriesAdditive(item, additive) {
  const keys = additiveKeysOf(item);
  if (keys) return keys.some((k) => additive.beKeys.includes(k));
  return !!additiveOf(item) && additive.compoundRe.test(`${item.name || ''} ${item.strength || ''}`);
}

export function moleculeOf(item) {
  const t = `${item?.name || ''} ${item?.medication || ''}`.toLowerCase();
  if (t.includes('tirzepatide')) return 'tirzepatide';
  if (t.includes('semaglutide')) return 'semaglutide';
  return 'other';
}

// Cross-pharmacy compound resolver: among SKUs carrying this additive on the
// same molecule, prefer the base's own baseProductKey group (same
// molecule/dose/supply), then same day-supply, then the base's own product
// line, then fewest extra additives (a plain +B6 beats +B6+B12 when only B6
// was asked for), then cheapest — so choosing B6 can swap a beluga base for
// e.g. southend's Semaglutide/Pyridoxine at the equivalent supply.
export function findCompound(additive, base, meds) {
  if (!additive || !base || !Array.isArray(meds)) return null;
  const candidates = meds.filter(
    (m) => m.medId !== base.medId && moleculeOf(m) === moleculeOf(base) && carriesAdditive(m, additive),
  );
  const rank = (m) =>
    (m.baseProductKey && base.baseProductKey && m.baseProductKey === base.baseProductKey ? -4 : 0) +
    (m.daySupply === base.daySupply ? 0 : 2) +
    (m.name === base.name ? 0 : 1) +
    ((additiveKeysOf(m) || [1]).length - 1);
  return (
    candidates.sort(
      (a, b) => rank(a) - rank(b) || (Number(a.price) || 0) - (Number(b.price) || 0),
    )[0] || null
  );
}
